import axios from "axios";
import moment from "moment";
import { GetAllAccountsAPI, GetAllChallansAPI, GetAllDebitsAPI, GetAllPurchasesAPI } from "../config/api";
import { pointToPointTemplate } from "../assets/html/pointToPointTemplate.js";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import _ from 'lodash';

/**
 * Modified version of pointToPointTemplate that returns a blob instead of opening in new window
 */
const generateChallanPDFBlob = (data, ctx = {}) => {
  let { challans, startDate, endDate, packingCharge: inputPackingCharge } = data;
  const packingCharge = Number(inputPackingCharge);
  const applyPackingCharge = !Number.isNaN(packingCharge) && packingCharge > 0;

  let rows = [];
  let clientName = "";
  let entityId = "";

  if (challans.length > 0) {
    clientName = challans[0].party.accountName;
    entityId = challans[0].entity;
  }
  let code = "";
  if (entityId === "62da46cf7eb6aa02db41297f") {
    code = `VO`;
  } else if (entityId === "62da46c57eb6aa02db41297d") {
    code = `IE`;
  } else {
    code = `VR`;
  }

  let endAmt = 0;
  let endQty = 0;

  challans.forEach((o, index) => {
    let createdAt = o["createdAt"];
    let totalQuantity = 0;
    let totalAmount = 0;

    rows.push([
      '', `${moment(createdAt).format("DD-MM-YYYY")}`,
      `${o.code}`, ``, '', '', '', '', '', '', ''
    ]);

    totalAmount += Number(o.amount) || 0;
    const pkgCost = Number(o.packagingCost);
    if (!Number.isNaN(pkgCost) && o.packagingCost != null && o.packagingCost !== "") {
      totalAmount += pkgCost;
    }
    endAmt += totalAmount;

    o.items.forEach((i, id) => {
      let {
        quantity,
        codeR,
        codeL,
        nameL,
        nameR,
        rProductCode,
        lProductCode,
        isPrescriptionOrder,
        rateR,
        rateL,
        discountPercent,
        discountAmount,
        fittingAmount,
        tint,
        orderRef,
        amount,
      } = i;

      const qty = Number(quantity);
      const safeQty = Number.isNaN(qty) ? 0 : qty;
      totalQuantity += safeQty;
      endQty += safeQty;

      const safeCodeSuffix = (code) => {
        if (code == null || code === "") return "";
        const s = String(code);
        const trimmed = s.length > 3 ? s.slice(3) : s;
        return ` (${trimmed})`;
      };

      // Match per-order P2P template behavior:
      // - Prefer nameR/nameL when present
      // - If prescription order, fall back to rProductCode/lProductCode
      const displayNameR =
        (nameR != null && String(nameR).trim() !== "")
          ? String(nameR)
          : (isPrescriptionOrder ? (rProductCode ?? "") : "");

      const displayNameL =
        (nameL != null && String(nameL).trim() !== "")
          ? String(nameL)
          : (isPrescriptionOrder ? (lProductCode ?? "") : "");

      let row = [
        id > 0 ? '' : index + 1,
        displayNameR ? `${displayNameR}${safeCodeSuffix(codeR)}` : '',
        displayNameL ? `${displayNameL}${safeCodeSuffix(codeL)}` : '',
        orderRef,
        quantity != null && quantity !== '' ? quantity : (safeQty || ''),
        rateR !== undefined && rateR !== null && rateR !== '' ? (Number(rateR) / 2).toFixed(2) : '',
        rateL !== undefined && rateL !== null && rateL !== '' ? (Number(rateL) / 2).toFixed(2) : '',
        discountPercent ? `${discountPercent}%` : discountAmount ? `Rs.${discountAmount}` : '',
        fittingAmount,
        tint,
        (Number(amount) != null && !Number.isNaN(Number(amount)) ? _.round(Number(amount), 2).toFixed(2) : '0.00')
      ];

      rows.push(row);

      if (id + 1 === o.items.length) {
        const safeTotalAmt = Number.isNaN(totalAmount) ? 0 : totalAmount;
        rows.push([
          '', '', '', 'Total', totalQuantity, '', '', '', '', '', _.round(safeTotalAmt, 2).toFixed(2), 'special']);
        // Display packing charges when present
        const pkgCost = Number(o.packagingCost);
        if (!Number.isNaN(pkgCost) && pkgCost > 0) {
          rows.push([
            '', '', '', 'Packing charges', '', '', '', '', '', '', _.round(pkgCost, 2).toFixed(2)]);
        }
      }
    });
  });

  if (applyPackingCharge) {
    endAmt += packingCharge;
  }

  const options = {
    orientation: "p",
    unit: "pt",
    format: [850, 1066],
    putOnlyUsedFonts: true,
  };

  const doc = ctx?.doc || new jsPDF(options);
  const sectionStartPage = ctx?.sectionStartPage ?? doc.internal.getNumberOfPages?.() ?? 1;

  doc.autoTable({
    startY: 50,
    head: [['Sr No.', 'Name R', 'Name L', 'Order Ref', 'Qty', 'Rate R', 'Rate L', 'Disc', 'Fitt Amt', 'Tint', 'Amount']],
    body: rows,
    styles: {
      fontSize: 12,
      cellPadding: 2,
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    },
    theme: 'grid',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0]
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240]
    },
    didDrawCell: function (data) {
      if (data.row.index === rows.length - 1 && data.column.index === 10 && data.cell.raw === 'special') {
        doc.setLineWidth(1.5);
        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height);
        doc.setLineWidth(0.1);
      }
    },
    didDrawPage: function (data) {
      let currentPage = 1;
      try {
        currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      } catch (_) {}

      const isSectionFirstPage = currentPage === sectionStartPage;
      if (isSectionFirstPage) {
        doc.setFontSize(19);
        doc.text('M/S: ' + clientName, 20, 25);
        doc.setFontSize(16);
        doc.text(code + ' Challan Summary', 300, 25);
        if (startDate && endDate) {
          doc.text(`Date:${moment(startDate).format("DD-MM-YYYY")} to ${moment(endDate).format("DD-MM-YYYY")}`, 600, 25);
        }
      }
      let footerStr = `Page ${currentPage}`;
      doc.text(footerStr, data.settings.margin.left, doc.internal.pageSize.height - 10);
    }
  });

  const secondTableStartY = doc.lastAutoTable.finalY;
  const summaryRows = [];
  if (applyPackingCharge) {
    summaryRows.push(['', '', '', '', '', '', '', '', '', 'Packing charges', _.round(packingCharge, 2).toFixed(2)]);
  }
  summaryRows.push(['', '', '', '', '', '', '', '', '', `Total Qty:${Number.isNaN(endQty) ? 0 : endQty}`, `Balance: ${Number.isNaN(endAmt) ? 0 : Math.round(endAmt)}`]);
  doc.autoTable({
    showHead: "never",
    head: [['Sr No.', 'Name R', 'Name L', 'Order Ref', 'Qty', 'Rate R', 'Rate L', 'Disc', 'Fitt Amt', 'Tint', 'Amount']],
    body: summaryRows,
    startY: secondTableStartY,
    startX: 5,
    styles: {
      fontStyle: 'bold',
      fontSize: 12,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      fillColor: [255, 255, 255]
    },
    headStyles: {
      fontStyle: 'bold',
      cellPadding: 0.75,
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      valign: 'middle',
      lineColor: [0, 0, 0],
      fontSize: 12,
    },
    footStyles: {
      cellPadding: 0.75,
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineWidth: 0.05,
      lineColor: [0, 0, 0]
    },
    tableLineWidth: 0.1,
    tableLineColor: [0, 0, 0],
    columnStyles: {
      10: { halign: 'right' },
      4: { halign: 'left' }
    }
  });

  if (ctx?.doc) return doc;
  return doc.output('blob');
};

/**
 * Find account by name (fuzzy matching)
 */
const findAccountByName = (accounts, companyName) => {
  const normalizedName = companyName.toLowerCase().trim();

  // Exact match first
  let match = accounts.find(acc =>
    acc.name?.toLowerCase().trim() === normalizedName ||
    acc.accountName?.toLowerCase().trim() === normalizedName
  );

  if (match) return match;

  // Partial match
  match = accounts.find(acc => {
    const accName = (acc.name || acc.accountName || '').toLowerCase().trim();
    return accName.includes(normalizedName) || normalizedName.includes(accName);
  });

  return match;
};

/**
 * Fetch challans for a company (same query shape as PointToPointChallan)
 */
const getChallansForCompany = async (company, startDate, endDate, entity, authToken) => {
  try {
    let challanss = [];

    // Same effective filter as Ledger: all challans in date range (billed + unbilled), no isBilled filter
    let query = `&userId=${company._id}&skip=undefined&limit=undefined`;
    if (startDate && endDate && moment(startDate).isValid() && moment(endDate).isValid()) {
      const sd = moment(startDate).format("DD-MM-YYYY");
      const ed = moment(endDate).format("DD-MM-YYYY");
      query += `&startDate=${sd}&endDate=${ed}`;
    }

    const res = await axios.get(GetAllChallansAPI(entity) + query, {
      headers: { Authorization: `Bearer ${authToken}` },
    });

    if (res.data.challans && res.data.challans.length > 0) {
      challanss = res.data.challans.reverse();
    }

    // If purchase type, also get debits (all in date range, same as Ledger)
    if (company.partyType === "purchase") {
      try {
        let debitQuery = `&userId=${company._id}&purchaseType=Challan`;
        if (startDate && endDate && moment(startDate).isValid() && moment(endDate).isValid()) {
          debitQuery += `&startDate=${moment(startDate).format("DD-MM-YYYY")}&endDate=${moment(endDate).format("DD-MM-YYYY")}`;
        }
        const debitRes = await axios.get(GetAllDebitsAPI(entity) + debitQuery, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (debitRes.data.debits && debitRes.data.debits.length > 0) {
          challanss = [...challanss, ...debitRes.data.debits.reverse()];
        }
      } catch (err) {
        console.error(`Error fetching debits for ${company.name || company.accountName}:`, err);
      }
    }

    return challanss;
  } catch (err) {
    console.error(`Error fetching challans for ${company.name || company.accountName}:`, err);
    return [];
  }
};

/**
 * Download PDF blob with filename
 */
const downloadPDF = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Generate P2P Challan PDFs for multiple companies
 *
 * @param {Array<{_id: string, name?: string, accountName?: string, partyType?: string}>|Array<string>} accountsOrCompanyNames - Array of account objects (preferred) or company name strings. When account objects are passed, the same accounts from the bulk modal are used so challan fetch matches row-click P2P.
 * @param {Date} startDate - Start date for the date range
 * @param {Date} endDate - End date for the date range
 * @param {string} entity - Entity ID
 * @param {string} authToken - Authentication token
 * @param {Function} onProgress - Optional progress callback
 * @param {number} [packingCharge] - Optional packing charge (INR) to add once per company to the PDF total
 * @returns {Promise<Object>} - { results, notFound, total, success, failed }
 */
export async function generateP2PChallansForCompanies(
  accountsOrCompanyNames,
  startDate,
  endDate,
  entity,
  authToken,
  onProgress,
  packingCharge
) {
  if (!accountsOrCompanyNames || accountsOrCompanyNames.length === 0) {
    throw new Error('No accounts or company names provided');
  }

  const isAccounts = Array.isArray(accountsOrCompanyNames) &&
    accountsOrCompanyNames.length > 0 &&
    typeof accountsOrCompanyNames[0] === 'object' &&
    accountsOrCompanyNames[0] !== null &&
    accountsOrCompanyNames[0]._id;

  let accounts = [];
  const notFound = [];

  if (isAccounts) {
    accounts = accountsOrCompanyNames;
  } else {
    // Resolve company names to accounts via API (fallback)
    let allAccounts = [];
    try {
      const res = await axios.get(GetAllAccountsAPI(entity), {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      allAccounts = res.data.accounts || [];
    } catch (err) {
      throw new Error(`Failed to fetch accounts: ${err.message}`);
    }
    for (const name of accountsOrCompanyNames) {
      const companyName = (typeof name === 'string' ? name : '').trim();
      const account = findAccountByName(allAccounts, companyName);
      if (account) {
        accounts.push(account);
      } else {
        notFound.push(companyName);
      }
    }
  }

  const results = [];
  const total = isAccounts ? accounts.length : accountsOrCompanyNames.length;
  const combinedDoc = new jsPDF({ orientation: "p", unit: "pt", format: [850, 1066], putOnlyUsedFonts: true });
  let hasAnySection = false;

  for (let i = 0; i < accounts.length; i++) {
    const account = accounts[i];
    const companyName = account.name || account.accountName || '';

    try {
      const challans = await getChallansForCompany(account, startDate, endDate, entity, authToken);

      if (!challans || challans.length === 0) {
        if (onProgress) {
          onProgress({
            company: companyName,
            status: 'skipped',
            reason: 'No challans found',
            index: i + 1,
            total: accounts.length
          });
        }
        continue;
      }

      const htmlData = {
        challans: challans,
        challanDate: moment().format("DD-MM-YYYY"),
        startDate,
        endDate,
        packingCharge: packingCharge != null && !Number.isNaN(Number(packingCharge)) ? Number(packingCharge) : 0
      };

      if (hasAnySection) {
        combinedDoc.addPage();
      }
      hasAnySection = true;
      const sectionStartPage = combinedDoc.internal.getNumberOfPages();
      generateChallanPDFBlob(htmlData, { doc: combinedDoc, sectionStartPage });

      results.push({
        company: companyName,
        account: account,
        filename: null,
        challanCount: challans.length,
        status: 'success'
      });

      if (onProgress) {
        onProgress({
          company: companyName,
          status: 'completed',
          index: i + 1,
          total: accounts.length,
          challanCount: challans.length
        });
      }

      if (i < accounts.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error(`Error generating PDF for ${companyName}:`, error);
      if (onProgress) {
        onProgress({
          company: companyName,
          status: 'error',
          error: error.message,
          index: i + 1,
          total: accounts.length
        });
      }
    }
  }

  if (hasAnySection) {
    const dateRange = (startDate && endDate && moment(startDate).isValid() && moment(endDate).isValid())
      ? `${moment(startDate).format("YYYY-MM-DD")}_${moment(endDate).format("YYYY-MM-DD")}`
      : moment().format("YYYY-MM-DD");
    const combinedFilename = `Bulk_P2P_Challans_${dateRange}.pdf`;
    const combinedBlob = combinedDoc.output('blob');
    downloadPDF(combinedBlob, combinedFilename);
  }

  return {
    results,
    notFound,
    total: isAccounts ? total : accountsOrCompanyNames.length,
    success: results.length,
    failed: notFound.length
  };
}

